/**
 * Index for machines that share one physical meter.
 *
 * Some single-phase machines hang off the same three-phase meter. The backend
 * tells them apart with `meter_index` (1, 2, 3... in DB order) on each reading,
 * and the dashboard has to rebuild the same numbering from the hierarchy tree
 * to look those readings up.
 *
 * The subtlety is what counts as "sharing". A multi-parent node is rendered
 * once under every parent it belongs to, so the same machine shows up in the
 * tree several times. Counting tree appearances would read those copies as two
 * different machines sharing a meter, index them 1 and 2, and then look up a
 * `<meter_id>_1` key the backend never sent — so the node renders with no data
 * at all. Distinct machine NAMES per meter is the thing to count, which leaves
 * a multi-parent node unindexed and looked up by its plain meter_id.
 */

/** Visit every node of the hierarchy tree, depth first. */
const walk = (node, visit) => {
  if (!node) return;
  visit(node);
  (node.children || []).forEach((child) => walk(child, visit));
};

/**
 * Map of machine_name → 1-based index, containing only machines that genuinely
 * share a meter with a differently-named machine. Everything else is absent,
 * which callers read as "look this up by meter_id alone".
 */
export const buildMeterIndexMap = (hierarchyTree) => {
  const indexMap = new Map();
  if (!hierarchyTree) return indexMap;

  // meter_id → the distinct machine names hanging off it.
  const namesByMeter = new Map();
  walk(hierarchyTree, (node) => {
    if (!node.meter_id) return;
    if (!namesByMeter.has(node.meter_id)) namesByMeter.set(node.meter_id, new Set());
    namesByMeter.get(node.meter_id).add(node.machine_name);
  });

  // Number them in tree order, which matches the DB order the backend used.
  const assigned = new Map(); // meter_id → how many names numbered so far
  walk(hierarchyTree, (node) => {
    const mid = node.meter_id;
    if (!mid) return;
    const names = namesByMeter.get(mid);
    if (!names || names.size < 2) return;          // not a shared meter
    if (indexMap.has(node.machine_name)) return;   // a repeat of a node already numbered
    const next = (assigned.get(mid) || 0) + 1;
    assigned.set(mid, next);
    indexMap.set(node.machine_name, next);
  });

  return indexMap;
};

/**
 * The key a reading is stored under: `<meter_id>_<index>` for a shared meter,
 * plain `<meter_id>` otherwise. Mirrors how the readings map is built from the
 * API's `meter_index`.
 */
export const readingKey = (meterId, meterIndexMap, machineName) => {
  if (!meterId) return null;
  const idx = meterIndexMap?.get(machineName);
  return idx != null ? `${meterId}_${idx}` : meterId;
};
